import { Table, type Cell, type Row } from '../ui/Block';
import { shortId } from './dataShared';

export interface CostCols { revenue: number; netRevenue: number; cost: number; estimated: number; margin: number; marginPct: number | null }
export type CostSource = 'recorded' | 'estimated' | 'none';
export interface CostLine { key: string; category: string; label: string; quantity: number; unit: string; unitPrice: number; usd: number; source: CostSource; note?: string; recording?: string }
export interface CostTotals { revenue: number; netRevenue: number; cost: number; estimated: number; margin: number; marginPct: number | null; paidIn?: number }

export const CATEGORIES: { key: string; label: string; short: string; bg: string }[] = [
  { key: 'engines', label: 'Fly engines', short: 'ENGINES', bg: 'var(--vx-1)' },
  { key: 'gpu', label: 'GPU pods', short: 'GPU PODS', bg: 'var(--vx-2)' },
  { key: 'llm', label: 'Language models', short: 'LLM', bg: 'var(--vx-3)' },
  { key: 'voices', label: 'Voices (free to customers)', short: 'VOICES', bg: 'var(--vx-4)' },
  { key: 'storage', label: 'Storage', short: 'STORAGE', bg: 'var(--vx-5)' },
  { key: 'relay', label: 'Relay / ingest machines', short: 'RELAY', bg: 'var(--vx-6)' },
  { key: 'egress', label: 'Egress', short: 'EGRESS', bg: 'var(--ink-28)' },
  { key: 'fixed', label: 'Fixed services', short: 'FIXED', bg: 'var(--ink-62)' },
];
export const catLabel = (k: string) => CATEGORIES.find((c) => c.key === k)?.label ?? k;

export const money = (x: number | null | undefined) => {
  if (x == null) return '—';
  const a = Math.abs(x);
  const s = a >= 1 || a === 0 ? a.toFixed(2) : a >= 0.01 ? a.toFixed(3) : a.toFixed(4);
  return `${x < 0 ? '−' : ''}$${s}`;
};
export const pct = (p: number | null | undefined) => (p == null ? '' : `${p > 0 ? '+' : ''}${Math.round(p)}%`);

export const revenueCell = (c: CostCols | null | undefined): Cell => (c ? { t: money(c.revenue), mono: true, v: c.revenue, sub: c.revenue ? `keep ${money(c.netRevenue)}` : 'not charged' } : { t: '—', fg: 'var(--ink-40)' });
export const costCell = (c: CostCols | null | undefined): Cell => (c ? { t: money(c.cost), mono: true, v: c.cost, sub: c.estimated ? `${money(c.estimated)} estimated` : 'all recorded' } : { t: '—', fg: 'var(--ink-40)' });
export const marginCell = (c: CostCols | null | undefined): Cell => (c
  ? { t: money(c.margin), mono: true, v: c.margin, fw: 600, fg: c.margin < 0 ? 'var(--conf-low)' : 'var(--ok)', sub: c.marginPct != null ? `${pct(c.marginPct)} margin` : c.margin < 0 ? 'nothing charged' : undefined }
  : { t: '—', fg: 'var(--ink-40)' });

const SRC: Record<CostSource, { label: string; tone: 'ok' | 'warn' | 'mute' }> = { recorded: { label: 'RECORDED', tone: 'ok' }, estimated: { label: 'ESTIMATED', tone: 'warn' }, none: { label: 'NOT RECORDED', tone: 'mute' } };
const qty = (q: number, unit: string) => `${q >= 100 ? Math.round(q).toLocaleString() : q >= 1 ? q.toFixed(2) : q.toPrecision(2)} ${unit}`;
const unitPrice = (p: number, unit: string) => {
  if (p === 0) return '$0';
  if (unit === 'tokens' || unit === 'chars') return `$${(p * 1e6).toFixed(2)}/M ${unit}`;
  return `${p >= 0.01 ? `$${p.toFixed(p >= 1 ? 2 : 4)}` : `$${p.toPrecision(2)}`}/${unit.replace(/s$/, '')}`;
};

export function CostLinesTable({ lines, mobile, recordings }: { lines: CostLine[]; mobile: boolean; recordings?: boolean }) {
  const rows: Row[] = lines.map((l, i) => ({
    key: `${l.recording ?? ''}:${l.key}:${i}`,
    cells: [
      { t: l.label, sub: l.note, fw: 500 },
      ...(recordings ? [{ t: l.recording ? shortId(l.recording) : '—', mono: true } as Cell] : []),
      { t: qty(l.quantity, l.unit), mono: true },
      { t: unitPrice(l.unitPrice, l.unit), mono: true },
      { pill: SRC[l.source] },
      { t: money(l.usd), mono: true, fw: 600, v: l.usd },
    ],
  }));
  return (
    <Table mobile={mobile} minWidth={recordings ? 760 : 660} rows={rows} cols={[
      { label: 'COST LINE', width: 'minmax(200px,2.2fr)' }, ...(recordings ? [{ label: 'RECORDING', width: '90px' }] : []),
      { label: 'QUANTITY', width: 'minmax(110px,1fr)' }, { label: 'UNIT PRICE', width: 'minmax(120px,1fr)' }, { label: 'SOURCE', width: '118px' }, { label: 'COST', width: '76px', align: 'r' },
    ]} />
  );
}
